<template>
  <div class="page-body">
    <div class="loading-panel-overlay">
      <div class="loading-panel">
        <div class="loading">
          <font-awesome-icon
            icon="spinner"
            pulse
          />
          <h4>{{ $t('paymentStatus.loading') }}</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive } from 'vue'
import { useDonationStore } from '@/stores/donation'
import { useHead } from '@vueuse/head'
import { usePaymentStore } from '@/stores/payment'
import { useRootStore } from '@/stores/root'
import awaitUpdate from '@/lib/donation/awaitUpdate'
import axios from 'axios'

export default {
  name: 'StripeAuthorisedCallback',
  setup () {
    const paymentStore = usePaymentStore()
    const donationStore = useDonationStore()
    const rootStore = useRootStore()
    const siteData = reactive({ title: '' })
    const stripe = null
    useHead({ title: computed(() => siteData.title) })

    return { siteData, rootStore, paymentStore, donationStore, stripe }
  },
  beforeMount () {
    this.siteData.title = `${this.$t('paymentStatus.loading')} - Stripe`
  },
  methods: {
    handleFailure (errMsg = null) {
      const queryObject = { payment_cancelled: true, failed_stripe_authorisation: true }
      const details = Object.assign({}, this.donationStore.details, { authorisation_error: errMsg })
      this.donationStore.setDetails(details)
      if (localStorage.location) { queryObject.l = localStorage.location }
      this.$router.push({ name: this.$t('routeNames.paymentDetails'), query: queryObject })
    },
    async processAuthorisation () {
      const reference = this.rootStore.queryParams.reference
      const paymentIntentClientSecret = this.rootStore.queryParams.payment_intent_client_secret
      const redirectStatus = this.rootStore.queryParams.redirect_status

      if (!reference) {
        this.handleFailure(this.$t('paymentStatus.failure'))
        return
      }

      if (redirectStatus === 'failed') {
        try { await axios.patch(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/failure`) } catch (_) { /* best effort */ }
        const config = this.$l10n.paymentMethods.stripe
        await this.$loadScript('https://js.stripe.com/v3/')
        this.stripe = new Stripe(process.env[config.apiKey], { locale: config.locale })
        let errMsg = this.$t('paymentStatus.failure')
        try {
          const intent = await this.stripe.retrievePaymentIntent(paymentIntentClientSecret)
          errMsg = intent.paymentIntent.last_payment_error.message
        } catch (_) { /* best effort */ }
        this.handleFailure(errMsg)
        return
      }

      try {
        await axios.post(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/confirm`)
      } catch (error) {
        if ('$bugsnag' in this) { this.$bugsnag.notify(error) }
        this.handleFailure(this.$t('paymentStatus.failure'))
        return
      }

      awaitUpdate(this, reference)
        .then((result) => {
          if (result.status === 'failed') {
            this.handleFailure(this.$t('paymentStatus.failure'))
          } else {
            this.$router.push({ name: this.$t('routeNames.thankYou') })
          }
        })
        .catch((error) => {
          if ('$bugsnag' in this) { this.$bugsnag.notify(error) }
          this.handleFailure(this.$t('paymentStatus.failure'))
        })
    }
  },
  mounted () {
    this.processAuthorisation()
  }
}
</script>

<style lang="scss" scoped>
  .page-body {
    color: white;
    position: relative;
    overflow: initial;
    background-color: white;
    max-height: initial;
    min-height: 60vh;
  }

  .loading-panel-overlay {
    background-color: rgba(black, 0.7);
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 5000;
    text-align: center;

    &:before {
      content: '';
      display: inline-block;
      height: 100%;
      vertical-align: middle;
    }

    .loading-panel {
      background-color: white;
      padding: 1em;
      display: inline-block;
      color: black;
      width: 40%;
      min-width: 300px;

      @include on-non-mobile(){
        min-width: 400px;
      }

      .fa-spinner {
        width: 2em;
        height: 2em;
      }

      h4 {
        margin: 0;
      }
    }
  }
</style>
